/**
 * PdfPageCanvas — renders a SINGLE PDF page to a `<canvas>` via pdfjs-dist (CB-2.2 / Task 2).
 *
 * This is the editor's page-render substrate (AD-15). The repo's only prior pdfjs use is text
 * extraction (knowledgeService.ts) — there was no `getViewport`/`page.render` pattern to reuse, so
 * this establishes it: getPage → getViewport({ scale }) → page.render({ canvasContext, viewport }).
 *
 * Lazy-render aware (UX-DR14): it rasterizes only when `active` (the host gates this via an
 * IntersectionObserver). It is DPR-aware (crisp text — also matters for AD-18 fidelity downstream)
 * and its RenderTask is cancellable — if the page scrolls out of the buffer before the render
 * resolves, the host flips `active` false and the in-flight task is cancelled, keeping the main
 * thread responsive on long docs (Task 3). A per-page render error is shown inline, never as a
 * whole-document failure.
 *
 * The host reserves this page's box (aspect-correct, from the pre-read intrinsic size) so scroll
 * height is correct before any rasterization; this component fills that box (width/height 100%).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import '@/lib/pdfWorker'; // bundled pdf.js worker (offline-safe; matches installed version)

function isRenderCancelled(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: string }).name === 'RenderingCancelledException';
}

interface PdfPageCanvasProps {
  pdf: PDFDocumentProxy;
  /** 0-based page index (the coordinate contract's page_index). */
  pageIndex: number;
  /** Target CSS width in px; render scale = displayWidth / intrinsic page width. */
  displayWidth: number;
  /** Lazy-render gate (UX-DR14): rasterize only when in/near the viewport. */
  active: boolean;
  /** Fired once this page's canvas finishes rasterizing (host uses it for NFR-10 first-paint timing). */
  onRendered?: (pageIndex: number) => void;
}

export function PdfPageCanvas({ pdf, pageIndex, displayWidth, active, onRendered }: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!active || displayWidth <= 0) return;

    let cancelled = false;
    let task: RenderTask | null = null;

    (async () => {
      try {
        const page = await pdf.getPage(pageIndex + 1); // pdfjs pages are 1-based
        if (cancelled) return;

        const intrinsic = page.getViewport({ scale: 1 });
        const dpr = window.devicePixelRatio || 1;
        const cssScale = displayWidth / intrinsic.width;
        const viewport = page.getViewport({ scale: cssScale * dpr });

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        // Backing store at device pixels; CSS box (100%) scales it back to the reserved page box.
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        // pdfjs v5 RenderParameters requires `canvas` (alongside the 2d context) — see pdfjs-dist 5.x.
        task = page.render({ canvas, canvasContext: ctx, viewport });
        await task.promise;
        if (cancelled) return;

        setHasError(false);
        setIsRendered(true);
        onRendered?.(pageIndex);
      } catch (err) {
        if (cancelled || isRenderCancelled(err)) return;
        console.error(`PdfPageCanvas: failed to render page ${pageIndex + 1}`, err);
        setHasError(true);
      }
    })();

    return () => {
      cancelled = true;
      try {
        task?.cancel();
      } catch {
        /* cancel after settle is a no-op */
      }
    };
  }, [pdf, pageIndex, displayWidth, active, onRendered]);

  const retry = useCallback(() => {
    setHasError(false);
    setIsRendered(false);
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-sm border border-border bg-white shadow-sm">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`Contract page ${pageIndex + 1}`}
        className="block h-full w-full"
      />

      {/* Per-page render shimmer (UX-DR21) — covers the canvas until this page rasterizes. */}
      {!isRendered && !hasError && (
        <div
          className="absolute inset-0 animate-pulse bg-muted"
          aria-hidden="true"
          data-testid={`page-shimmer-${pageIndex}`}
        />
      )}

      {/* Inline per-page error — does NOT fail the whole document. */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/40 p-4 text-center">
          <p className="text-sm text-muted-foreground">Couldn’t render page {pageIndex + 1}.</p>
          <button
            type="button"
            onClick={retry}
            className="text-sm font-medium text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
